import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { User, Mail, Lock } from 'lucide-react';

const LoginPage = ({ userType = 'client' }) => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(formData.email, formData.password);
      
      toast.success('Login Successful!', {
        description: `Welcome back, ${user.full_name}!`
      });

      // Redirect based on role
      if (user.role === 'landlord') {
        navigate('/landlord/dashboard');
      } else if (user.role === 'admin') {
        navigate('/super-admin');
      } else {
        navigate('/client/dashboard');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Login Failed', {
        description: error.response?.data?.detail || 'Invalid email or password'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#f8f9fa' }}>
      <Navbar />
      <Toaster position="top-center" richColors />

      <section className="py-20">
        <div className="container mx-auto px-4 max-w-md">
          <Card className="rounded-2xl shadow-2xl border-2" style={{ borderColor: '#e8e8e8' }}>
            <CardContent className="p-10">
              <div className="text-center mb-8">
                <div className="inline-block p-4 rounded-full mb-4" style={{ background: 'linear-gradient(135deg, rgba(212,165,116,0.1), rgba(232,201,161,0.1))' }}>
                  <User size={48} style={{ color: '#d4a574' }} />
                </div>
                <h1 className="text-4xl font-bold mb-2" style={{ color: '#1a1a2e' }}>
                  {userType === 'admin' ? 'Admin Login' : userType === 'landlord' ? 'Landlord Login' : 'Client Login'}
                </h1>
                <p style={{ color: '#636e72' }}>
                  {userType === 'admin'
                    ? 'Super admin access to manage the entire platform'
                    : userType === 'landlord' 
                    ? 'Access your property management dashboard'
                    : 'Access your account to place bids and manage bookings'
                  }
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="email" className="flex items-center gap-2 mb-2">
                    <Mail size={16} style={{ color: '#d4a574' }} />
                    <span style={{ color: '#2d3436' }}>Email Address</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="you@example.com"
                    className="h-12 rounded-xl border-2 focus:border-[#d4a574]"
                  />
                </div>

                <div>
                  <Label htmlFor="password" className="flex items-center gap-2 mb-2">
                    <Lock size={16} style={{ color: '#d4a574' }} />
                    <span style={{ color: '#2d3436' }}>Password</span>
                  </Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    placeholder="••••••••"
                    className="h-12 rounded-xl border-2 focus:border-[#d4a574]"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary-custom h-12 rounded-full text-lg font-semibold"
                >
                  {loading ? 'Logging in...' : 'Login'}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p style={{ color: '#636e72' }}>
                  Don't have an account?{' '}
                  <Link 
                    to={userType === 'admin' ? '/admin/register' : userType === 'landlord' ? '/landlord/register' : '/register'}
                    className="font-semibold"
                    style={{ color: '#d4a574' }}
                  >
                    Register here
                  </Link>
                </p>
              </div>

              {userType === 'client' && (
                <div className="mt-4 text-center space-y-2">
                  <Link to="/landlord/login" className="text-sm block" style={{ color: '#636e72' }}>
                    Are you a landlord? Login here
                  </Link>
                  <Link to="/admin/login" className="text-sm block" style={{ color: '#636e72' }}>
                    Admin? Login here
                  </Link>
                </div>
              )}
              
              {userType === 'landlord' && (
                <div className="mt-4 text-center space-y-2">
                  <Link to="/login" className="text-sm block" style={{ color: '#636e72' }}>
                    Are you a client? Login here
                  </Link>
                  <Link to="/admin/login" className="text-sm block" style={{ color: '#636e72' }}>
                    Admin? Login here
                  </Link>
                </div>
              )}
              
              {userType === 'admin' && (
                <div className="mt-4 text-center space-y-2">
                  <Link to="/login" className="text-sm block" style={{ color: '#636e72' }}>
                    Client? Login here
                  </Link>
                  <Link to="/landlord/login" className="text-sm block" style={{ color: '#636e72' }}>
                    Landlord? Login here
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LoginPage;